<template>
    <div class="spinner-container">
      <div 
        class="spinner-wrapper" 
        :style="wrapperStyle"
      >
        <div 
          v-for="(dot, index) in 8" 
          :key="index"
          class="dot"
          :style="getDotStyle(index)"
        ></div>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, computed, onMounted } from 'vue';
  
  // Configuration based on your Figma specs
  const BOUNCY_BEZIER = 'cubic-bezier(0.91, -0.55, 0.16, 1.6)';
  const EXPAND_DURATION = 600; // ms
  const SPIN_DURATION = 100;   // ms
  const DELAY = 1;             // ms (negligible in JS, used for next tick)
  
  // State definition
  // 1 = Collapsed (Start)
  // 2 = Expanded
  // 3 = Rotated 1
  // 4 = Rotated 2
  // 5 = Rotated 3 (Before collapsing back)
  const currentStep = ref(1);
  
  // We track total rotation to ensure the spinner always spins clockwise
  // instead of resetting back to 0 (which would cause a counter-spin).
  const totalRotation = ref(0);
  
  // Helper to wait (promisified setTimeout)
  const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));
  
  const runAnimationLoop = async () => {
    while (true) {
      // --- Step 1 to 2: EXPANSION (Bouncy) ---
      // User: Duration 600ms, Bouncy Bezier
      await wait(DELAY);
      currentStep.value = 2;
      // No rotation change here, just expansion
      await wait(EXPAND_DURATION);
  
      // --- Step 2 to 3: SPIN 1 (Linear) ---
      // User: Duration 100ms, Linear
      await wait(DELAY);
      currentStep.value = 3; 
      totalRotation.value += 45; // Rotate 45deg
      await wait(SPIN_DURATION);
  
      // --- Step 3 to 4: SPIN 2 (Linear) ---
      // User: Duration 100ms, Linear
      await wait(DELAY);
      currentStep.value = 4;
      totalRotation.value += 45;
      await wait(SPIN_DURATION);
  
      // --- Step 4 to 5: SPIN 3 (Linear) ---
      // User: Duration 100ms, Linear
      await wait(DELAY);
      currentStep.value = 5;
      totalRotation.value += 45;
      await wait(SPIN_DURATION);
  
      // --- Step 5 to 1: COLLAPSE (Bouncy) ---
      // User: Duration 600ms, Bouncy Bezier (Same as 1->2)
      // We rotate one last time to align the cross shape while collapsing
      await wait(DELAY);
      currentStep.value = 1;
      totalRotation.value += 45; 
      await wait(EXPAND_DURATION);
    }
  };
  
  onMounted(() => {
    runAnimationLoop();
  });
  
  // --- Dynamic Styles ---
  
  const wrapperStyle = computed(() => {
    // Determine if we are in a "Bouncy" phase or a "Linear" phase
    const isBouncy = currentStep.value === 2 || currentStep.value === 1;
    
    const duration = isBouncy ? EXPAND_DURATION : SPIN_DURATION;
    const easing = isBouncy ? BOUNCY_BEZIER : 'linear';
  
    return {
      transform: `rotate(${totalRotation.value}deg)`,
      transition: `all ${duration}ms ${easing}`,
    };
  });
  
  const getDotStyle = (index) => {
  // Radius configuration
  const isCollapsed = currentStep.value === 1;
  const radius = isCollapsed ? 12 : 32; // px
  
  // Angle: Distribute 8 dots (45 degrees apart)
  const angle = index * 45;

  // --- NEW: Angular Gradient Logic ---
  // We map the 4 colors from your screenshot to the cardinal directions
  // and interpolate the mix for the diagonals to create a smooth sweep.
  // 0deg = Right (CSS standard), so we arrange the array to match the clock positions.
  const colors = [
    '#25B8C2', // Index 0: Right (90deg in gradient terms)
    '#3D9FC0', // Index 1: Mix (Right-Bottom)
    '#5587BC', // Index 2: Bottom
    '#2A679A', // Index 3: Mix (Bottom-Left)
    '#004879', // Index 4: Left
    '#4582A0', // Index 5: Mix (Left-Top)
    '#8BBDC7', // Index 6: Top (Start of Gradient)
    '#58BBC5', // Index 7: Mix (Top-Right)
  ];

  // Logic for the "Cross" shape in collapsed state
  const isDiagonal = index % 2 !== 0;
  const scale = (isCollapsed && isDiagonal) ? 0 : 1;
  const opacity = (isCollapsed && isDiagonal) ? 0 : 1;

  // Animation timing
  const isBouncy = currentStep.value === 2 || currentStep.value === 1;
  const duration = isBouncy ? EXPAND_DURATION : SPIN_DURATION;
  const easing = isBouncy ? BOUNCY_BEZIER : 'linear';

  return {
    backgroundColor: colors[index],
    transform: `rotate(${angle}deg) translate(${radius}px) scale(${scale})`,
    opacity: opacity,
    transition: `all ${duration}ms ${easing}`,
  };
};
  </script>
  
  <style scoped>
  .spinner-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
    background-color: #f0f0f0; /* Match background context */
  }
  
  .spinner-wrapper {
    position: relative;
    width: 0;
    height: 0;
  }
  
  .dot {
    position: absolute;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    /* Center the dot on its own coordinate before transforming */
    top: -6px; 
    left: -6px;
  }
  </style>